import * as React from 'react';
import { useEffect, useState } from 'react';

import { defaultWrapLinesPreference, mediumPageSizePreference, paginationLabels } from 'src/commons/tables';

import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';

import { useCollection } from '@amzn/awsui-collection-hooks';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';

import { listAllReportingTables } from 'src/api/metricstore';

import { MetricStorePageHeader } from 'src/components/metricstore/components';

import { UnauthorizedModal } from 'src/components/metricstore/components-V3';
import { createReportingTableDefinitionLink } from 'src/routes/Paths';
import { EmptyState } from 'src/commons/EmptyState';
import { TABLE_CONTENT_TYPE } from 'src/commons/constants';

export interface ReportingTableCatalogProps {
  setContentType: any;
}

export const ReportingTableCatalog = (props: ReportingTableCatalogProps) => {
  const [allItems, setItems] = useState([]);
  const [loadingReportingTableCatalog, setLoadingReportingTableCatalog] = useState(false);
  const [tableMessage, setTableMessage] = useState('No reporting table');
  const [onboardModal, setOnboardModal] = useState(false);

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (item) => <Link href={createReportingTableDefinitionLink(item.id)}>{item.name}</Link>,
      minWidth: 300,
      sortingField: 'name',
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item) => item.description,
      minWidth: 300,
      sortingField: 'description',
    },
    {
      id: 'ownerTeam',
      header: 'Owner team',
      cell: (item) => item.ownerTeam,
      minWidth: 300,
      sortingField: 'ownerTeam',
    },
    {
      id: 'isArchived',
      header: 'Status',
      cell: (item) => (item.isArchived ? 'Inactive' : 'Active'),
      minWidth: 300,
      sortingField: 'isArchived',
    },
  ];

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  /**
   * Asynchronously fetches and stores the metrics catalog
   */
  const handleRefresh = async () => {
    setLoadingReportingTableCatalog(true);

    try {
      const ListAllReportingTablesResponse = await listAllReportingTables({});
      setItems(ListAllReportingTablesResponse.reportingtables);
    } catch (err) {
      if (err.name == 'InsufficientPermissionException') {
        setOnboardModal(true);
      }
      setTableMessage(`No reporting table to display.`);
    }
    setLoadingReportingTableCatalog(false);
  };

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(allItems, {
    filtering: {},
    pagination: { pageSize: 25 },
    sorting: {
      defaultState: {
        sortingColumn: {
          sortingField: 'name',
        },
      },
    },
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  return (
    <>
      <UnauthorizedModal visible={onboardModal} onClose={() => setOnboardModal(false)} />
      <SpaceBetween size={'s'}>
        <Header variant='h1' description={'This page lists all reporting table created using MetricStore.'}>
          Reporting table catalog
        </Header>
        <Table
          {...collectionProps}
          loadingText='Loading reporting table catalog...'
          loading={loadingReportingTableCatalog}
          columnDefinitions={columnDefinitions}
          items={items}
          wrapLines={preferences.wrapLines}
          resizableColumns={true}
          header={
            <>
              <MetricStorePageHeader
                buttons={[
                  {
                    text: '',
                    icon: 'refresh',
                    onItemClick: handleRefresh,
                  },
                ]}
                counter={`(${items.length})`}
                subheader={'Reporting table catalog'}
              />
            </>
          }
          filter={
            <TextFilter
              {...filterProps}
              filteringAriaLabel='Search reporting table'
              filteringPlaceholder='Search reporting table'
              countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
            />
          }
          empty={<EmptyState title={tableMessage} subtitle='No reporting table' />}
          pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
          preferences={
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={mediumPageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          }
        />
      </SpaceBetween>
    </>
  );
};
